import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { cn } from '@/lib/utils';
import { useCallback, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Dialog,
} from '@/components/ui/dialog';
import { useTranslation } from '../../../hooks/useTranslation';
import { useCreateInventory } from '../../../hooks/useInventories';
import { toast } from '@/hooks/use-toast';
import { CreateNonDefaultForm } from './CreateNonDefaultForm';
import type { CreateInventoryFormData } from '../../../schemas/inventory.schema';
import { INVENTORY_CATEGORIES } from '@api/inventories';

const INVENTORY_CATEGORIES_LIST = Object.values(INVENTORY_CATEGORIES).sort((a, b) => a.localeCompare(b));

// Source: app/models/inventory.rb - Inventory::Locations::ALL
const INVENTORY_LOCATIONS = [
  'Warehouse',
  'HQ',
  'Ops Area (Racks)',
  'Ops HQ (Look for Ops Executive)',
  'Store',
  'Kitchen',
  'Live Station Shelf',
].sort((a, b) => a.localeCompare(b));

const TOAST_DURATION = 10_000;

interface CreateInventoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CreateInventoryDialogContent({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const [images, setImages] = useState<File[]>([]);

  const handleClose = useCallback(() => {
    setImages([]);
    onOpenChange(false);
  }, [onOpenChange]);

  const createInventory = useCreateInventory({
    onSuccess: (inventory) => {
      const { dismiss } = toast({
        description: (
          <span className="flex min-w-0 items-center gap-1">
            <Link
              to="/inventories/$id/edit"
              params={{ id: String(inventory.id) }}
              className={cn('truncate', UNDERLINE_STATIC)}
              onClick={() => dismiss()}
            >
              {inventory.name}
            </Link>
            <span className="shrink-0">{t('create_modal.create_form.saved_label')}</span>
          </span>
        ),
        duration: TOAST_DURATION,
      });
      handleClose();
    },
  });

  const handleCreate = useCallback(async (values: CreateInventoryFormData) => {
    await createInventory.mutateAsync({
      name: values.name,
      category: values.category,
      location: values.location,
      recommended_retail_price: values.recommended_retail_price ?? null,
      cost_to_company: values.cost_to_company ?? null,
      ...(images.length > 0 ? { images } : {}),
    });
  }, [createInventory, images]);

  return (
    <CreateNonDefaultForm
      categories={INVENTORY_CATEGORIES_LIST}
      locations={INVENTORY_LOCATIONS}
      isPending={createInventory.isPending}
      images={images}
      onImagesChange={setImages}
      onSubmit={handleCreate}
      onClose={handleClose}
    />
  );
}

export function CreateInventoryDialog({
  open,
  onOpenChange,
}: CreateInventoryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <CreateInventoryDialogContent onOpenChange={onOpenChange} />
      )}
    </Dialog>
  );
}
